import {
  CalendarDateTime,
  now,
  getLocalTimeZone,
} from "@internationalized/date";
import { useCallback, useMemo, useState } from "react";

interface HasChecklistType {
  checklistType: string;
}

interface HasExecutedAt {
  executedAt: CalendarDateTime;
}

export const useChecklistFilters = <T extends HasChecklistType & HasExecutedAt>(
  fillOuts: T[],
) => {
  const [filter, setFilter] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);

  const filteredFillOuts = useMemo(() => {
    const now24HoursAgo = now(getLocalTimeZone()).subtract({ hours: 24 });

    // First filter by 24-hour window
    // A fill out is only valid for 24 hours
    const recentFillOuts = fillOuts.filter(
      ({ executedAt }) => executedAt.compare(now24HoursAgo) >= 0,
    );

    return filter.length
      ? recentFillOuts.filter(({ checklistType }) =>
          filter.includes(checklistType),
        )
      : recentFillOuts;
  }, [filter, fillOuts]);

  const displayedFillOuts = useMemo(
    () => (showAll ? filteredFillOuts : filteredFillOuts.slice(0, 8)),
    [filteredFillOuts, showAll],
  );

  const handleFilterToggle = useCallback((filterToToggle: string) => {
    setFilter((previousFilters) =>
      previousFilters.includes(filterToToggle)
        ? previousFilters.filter(
            (previousFilter) => previousFilter !== filterToToggle,
          )
        : [...previousFilters, filterToToggle],
    );
  }, []);

  const toggleShowAll = useCallback(() => {
    setShowAll((prev) => !prev);
  }, []);

  return {
    filter,
    showAll,
    filteredFillOuts,
    displayedFillOuts,
    handleFilterToggle,
    toggleShowAll,
  };
};
